// =============================================================================
// AcceptanceHubScreen — /accept/:token (PUBLIC, no auth)
//
// Client-facing landing page for a HOP submission. Magic-token in URL is the
// only credential. Worker (api-gateway /accept/:token) validates it server-
// side and returns the submission JSON with pre-signed download URLs.
//
// Layout (all tasteful, single-page, no AppShell):
//   - Coretel banner header (orange band + logo)
//   - Project header: code, name, site, client
//   - Status pill (Sent / Opened / Downloaded / Signed)
//   - Photo bundles — one tile per bundle, total size, expandable list of
//     photos with their friendly Site_Code_Category_NN filename + per-photo
//     download. Each photo download triggers a "downloaded" event.
//   - Documents — typed list with download buttons
//   - "I accept this submission" button at the bottom — captures contact
//     email + timestamp + IP server-side
//   - Branded footer
// =============================================================================

import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import JSZip from "jszip";
import {
  CheckCircle2, Download, FolderOpen, FileText, ShieldCheck,
  AlertTriangle, Calendar, Clock, MapPin, Building2, Package, Loader2,
} from "lucide-react";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.coretelgroup.com";

interface AcceptanceData {
  submission: {
    submission_number: string;
    title:             string | null;
    sent_at:           string | null;
    expires_at:        string | null;
    signed_at:         string | null;
  };
  project: {
    project_code: string;
    project_name: string;
    status:       string;
    client:       { legal_name: string; trading_name: string | null } | null;
    site:         { site_name: string; site_code: string; suburb: string; state: string } | null;
  } | null;
  site_code: string | null;
  photo_bundles: {
    bundle_number:    number;
    photos: {
      asset_id:      string;
      filename:      string;
      size_bytes:    number | null;
      mime_type:     string | null;
      download_url:  string | null;
    }[];
    total_size_bytes: number;
  }[];
  documents: {
    submission_doc_id: string;
    doc_type:          string;
    doc_type_label:    string | null;
    filename:          string;
    size_bytes:        number | null;
    download_url:      string | null;
  }[];
}

// -----------------------------------------------------------------------------

// T-205 v1 ships Download All only — size picker was prototyped but reverted
// 2026-05-07 because Cloudflare Image Resizing requires same-zone source
// images and R2 signed URLs are on a different zone. Proper resize comes
// later via T-242 (server-side worker re-serve OR client-side canvas).

const DOC_TYPE_LABELS: Record<string, string> = {
  rlm_pdf:       "Red Line Markup (PDF)",
  rlm_xlsx:      "As-Built / RLM (Excel)",
  sweep_test:    "Sweep test (Anritsu)",
  concrete_test: "Concrete test report",
  form_16:       "Form 16 — Engineering sign-off",
  other:         "Other document",
};

function fmtBytes(n: number | null | undefined): string {
  if (!n) return "—";
  const mb = n / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// -----------------------------------------------------------------------------

export default function AcceptanceHubScreen() {
  const { token } = useParams<{ token: string }>();
  const [data,    setData]    = useState<AcceptanceData | null>(null);
  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Sign + acceptance state
  const [signing,    setSigning]    = useState(false);
  const [signEmail,  setSignEmail]  = useState("");
  const [signedAt,   setSignedAt]   = useState<string | null>(null);

  // T-205 v1 — Download all (all photos + all docs in one ZIP). Original
  // resolution only for now; size picker comes back when T-242 lands resize.
  const [downloadAllInProgress, setDownloadAllInProgress] = useState(false);
  const [downloadAllProgress,   setDownloadAllProgress]   = useState<{ done: number; total: number } | null>(null);

  // Initial fetch + record "opened" event
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`${API_BASE_URL}/accept/${token}`);
        if (cancelled) return;
        if (res.status === 410) {
          const body = await res.json();
          setError(body.error === "expired" ? "This link has expired." : "This submission has been cancelled.");
          setLoading(false);
          return;
        }
        if (!res.ok) {
          setError("This link is not valid. Check with your Coretel contact for a new one.");
          setLoading(false);
          return;
        }
        const payload = await res.json() as AcceptanceData;
        setData(payload);
        setSignedAt(payload.submission.signed_at);
        setLoading(false);

        // Fire-and-forget — record that this link was opened
        fetch(`${API_BASE_URL}/accept/${token}/event`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ event: "opened" }),
        }).catch(() => {});
      } catch {
        if (!cancelled) {
          setError("Couldn't reach Coretel's servers. Try again in a minute.");
          setLoading(false);
        }
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [token]);

  function handleDownload(_label: string) {
    // Fire-and-forget — record that a download happened.
    fetch(`${API_BASE_URL}/accept/${token}/event`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ event: "downloaded" }),
    }).catch(() => {});
  }

  async function handleSign() {
    setSigning(true);
    try {
      const res = await fetch(`${API_BASE_URL}/accept/${token}/sign`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ contact_email: signEmail.trim() || undefined }),
      });
      if (res.ok) {
        const body = await res.json();
        setSignedAt(body.signed_at);
      }
    } finally {
      setSigning(false);
    }
  }

  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted">
        Loading submission…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg p-4">
        <div className="max-w-md w-full bg-ink-card border border-line rounded-lg p-6 text-center">
          <AlertTriangle size={32} className="mx-auto text-warn mb-3"/>
          <div className="text-[15px] font-medium text-text mb-1">
            {error ?? "Submission unavailable"}
          </div>
          <div className="text-[12px] text-text-muted">
            If you believe this is wrong, contact your Coretel project manager.
          </div>
        </div>
      </div>
    );
  }

  const totalPhotos = data.photo_bundles.reduce((acc, b) => acc + b.photos.length, 0);
  const totalDataMb = data.photo_bundles.reduce((acc, b) => acc + b.total_size_bytes, 0) / (1024 * 1024);

  return (
    <div className="min-h-screen bg-bg">
      {/* T-204 — Branded header band. Coretel "C" symbol rendered in cream
          (text-orange-50 ≈ #fff7ed) sits cleanly against the orange band — no
          white-box backdrop. Inline SVG so we don't depend on a hosted asset. */}
      <div className="bg-accent text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12 flex items-center gap-4 md:gap-6">
          <svg
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Coretel"
            className="text-orange-50 flex-shrink-0 h-16 md:h-20 w-16 md:w-20"
          >
            <path
              fill="currentColor"
              fillRule="evenodd"
              d="M26.354 5.591 31.819 0H0.18v32h31.639l-5.465-5.591H5.658V5.591h20.696Zm-0.211 21.318 4.488 4.591H0.68v-31h29.951l-4.488 4.591H5.158v21.818h20.985Z"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] md:text-[12px] font-mono uppercase tracking-wider2 text-white/85">
              Acceptance Hub
            </div>
            <div className="text-xl md:text-2xl font-semibold truncate">
              {data.submission.title ?? data.submission.submission_number}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Status banner */}
        {signedAt ? (
          <div className="mb-5 px-4 py-3 bg-ok/10 border border-ok/30 rounded-md flex items-center gap-3">
            <CheckCircle2 size={18} className="text-ok flex-shrink-0"/>
            <div>
              <div className="text-[13px] font-medium text-ok">Submission accepted</div>
              <div className="text-[11px] text-text-muted">{fmtDate(signedAt)}</div>
            </div>
          </div>
        ) : (
          <div className="mb-5 px-4 py-3 bg-accent/8 border border-accent/30 rounded-md text-[12px] text-text leading-relaxed">
            <div className="font-medium text-text mb-1">Welcome — first time on the Coretel Acceptance Hub?</div>
            Below you'll find the photos and documents Coretel has compiled for
            this site's HOP submission. Download what you need — bundles are
            grouped for convenience. When you're satisfied, click <strong>Accept</strong>
            at the bottom to confirm receipt.
          </div>
        )}

        {/* Project meta */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-ink-card border border-line rounded-md p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Project</div>
            <div className="text-[14px] font-medium text-text leading-tight">
              {data.project?.project_name ?? "—"}
            </div>
            <div className="text-[11px] text-text-muted font-mono mt-0.5">
              {data.project?.project_code}
            </div>
            {data.project?.client && (
              <div className="text-[12px] text-text-muted mt-2 flex items-center gap-1.5">
                <Building2 size={12}/>
                {data.project.client.trading_name ?? data.project.client.legal_name}
              </div>
            )}
          </div>

          <div className="bg-ink-card border border-line rounded-md p-4">
            <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Site</div>
            <div className="text-[14px] font-medium text-text leading-tight">
              {data.project?.site?.site_name ?? data.site_code ?? "—"}
            </div>
            <div className="text-[11px] text-text-muted font-mono mt-0.5">
              {data.site_code}
            </div>
            {data.project?.site && (
              <div className="text-[12px] text-text-muted mt-2 flex items-center gap-1.5">
                <MapPin size={12}/>
                {data.project.site.suburb}, {data.project.site.state}
              </div>
            )}
          </div>
        </div>

        {/* Photo bundles */}
        {data.photo_bundles.length > 0 && (
          <div className="mb-6">
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted">
                  Photo bundles
                </div>
                <div className="text-[11px] text-text-muted mt-1">
                  {totalPhotos} photo{totalPhotos === 1 ? "" : "s"} · {totalDataMb.toFixed(0)} MB total at original
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={async () => {
                    if (downloadAllInProgress || !data) return;
                    setDownloadAllInProgress(true);
                    handleDownload("Download all (full pack ZIP)");
                    try {
                      const allPhotos = data.photo_bundles.flatMap(b => b.photos);
                      const allDocs = data.documents;
                      const total = allPhotos.length + allDocs.length;
                      setDownloadAllProgress({ done: 0, total });

                      const zip = new JSZip();
                      let done = 0;

                      await Promise.all([
                        ...allPhotos.map(async p => {
                          if (!p.download_url) return;
                          const res = await fetch(p.download_url);
                          if (!res.ok) throw new Error(`Photo fetch ${p.filename}: ${res.status}`);
                          zip.file(`photos/${p.filename}`, await res.blob());
                          done += 1;
                          setDownloadAllProgress({ done, total });
                        }),
                        ...allDocs.map(async d => {
                          if (!d.download_url) return;
                          const res = await fetch(d.download_url);
                          if (!res.ok) throw new Error(`Doc fetch ${d.filename}: ${res.status}`);
                          zip.file(`documents/${d.filename}`, await res.blob());
                          done += 1;
                          setDownloadAllProgress({ done, total });
                        }),
                      ]);

                      const zipBlob = await zip.generateAsync({ type: "blob" });
                      const sitePart = (data.site_code ?? "SITE").replace(/[^A-Za-z0-9-]/g, "_");
                      const today    = new Date().toISOString().slice(0, 10);
                      const zipName  = `${sitePart}_FullPack_${today}.zip`;

                      const url = URL.createObjectURL(zipBlob);
                      const a = document.createElement("a");
                      a.href = url; a.download = zipName; a.target = "_blank"; a.rel = "noopener";
                      document.body.appendChild(a); a.click(); a.remove();
                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                    } catch (err) {
                      console.error("[download all] failed:", err);
                      alert("Download failed mid-way. Try downloading bundles individually.");
                    } finally {
                      setDownloadAllInProgress(false);
                      setDownloadAllProgress(null);
                    }
                  }}
                  disabled={downloadAllInProgress}
                  className="text-[12px] px-3 py-1.5 rounded bg-accent text-white hover:bg-accent-bright disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {downloadAllInProgress
                    ? `Zipping ${downloadAllProgress?.done ?? 0} / ${downloadAllProgress?.total ?? 0}…`
                    : `↓ Download all`}
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {data.photo_bundles.map(b => (
                <BundleTile
                  key={b.bundle_number}
                  bundle={b}
                  siteCode={data.site_code}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          </div>
        )}

        {/* Documents */}
        {data.documents.length > 0 && (
          <div className="mb-6">
            <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-3">
              Documents
            </div>
            <div className="space-y-2">
              {data.documents.map(d => (
                <DocRow key={d.submission_doc_id} doc={d} onDownload={handleDownload}/>
              ))}
            </div>
          </div>
        )}

        {/* Sign block — wording is "acknowledge receipt" not "accept the
            work". Client downloading isn't equivalent to accepting; this
            button just records that they've seen and pulled down what we
            sent. Approval / sign-off is a separate process on their end. */}
        {!signedAt && (
          <div className="mt-8 p-5 bg-ink-card border border-line rounded-md">
            <div className="text-[14px] font-medium text-text mb-2">
              Acknowledge receipt of Coretel's HOP submission
            </div>
            <p className="text-[12px] text-text-muted mb-4 leading-relaxed">
              Click below once you've downloaded what you need.
              This isn't an approval — it just confirms you received the
              photos and documents. Optional: enter your email and Coretel
              will record which contact acknowledged it.
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="email"
                value={signEmail}
                onChange={e => setSignEmail(e.target.value)}
                placeholder="your.email@client.com (optional)"
                className="flex-1 px-3 py-2 text-[13px] bg-ink border border-line rounded text-text"
              />
              <button
                onClick={handleSign}
                disabled={signing}
                className="px-5 py-2 bg-accent text-white rounded font-medium text-[13px] hover:bg-accent-hover transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                <ShieldCheck size={14}/>
                {signing ? "Recording…" : "Confirm received"}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-line text-[11px] text-text-dim text-center">
          Submission {data.submission.submission_number} ·
          Sent {fmtDate(data.submission.sent_at)} ·
          {data.submission.expires_at && <> Expires {fmtDate(data.submission.expires_at)}</>}
        </div>
        <div className="mt-3 text-[10px] text-text-dim text-center">
          Coretel Group · ops.coretelgroup.com
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bundle + Doc tiles
// -----------------------------------------------------------------------------

function BundleTile({
  bundle, siteCode, onDownload,
}: {
  bundle:     AcceptanceData["photo_bundles"][number];
  siteCode:   string | null;
  onDownload: (label: string) => void;
}) {
  const [open,        setOpen]        = useState(false);
  const [zipping,     setZipping]     = useState(false);
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);
  const sizeMb = bundle.total_size_bytes / (1024 * 1024);

  async function downloadBundleAsZip() {
    if (zipping) return;
    setZipping(true);
    setZipProgress({ done: 0, total: bundle.photos.length });
    onDownload(`Bundle ${bundle.bundle_number} ZIP`);
    try {
      const zip = new JSZip();
      let done = 0;
      const failed: string[] = [];
      // T-418 — per-photo retries + partial tolerance (the T-379 lessons):
      // one flaky fetch no longer kills the whole bundle, and failures are
      // named in the zip instead of silently missing.
      await Promise.all(bundle.photos.map(async (p) => {
        if (!p.download_url) { failed.push(`${p.filename} (no URL)`); return; }
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const res = await fetch(p.download_url, { cache: "no-store" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            zip.file(p.filename, blob);
            done += 1;
            setZipProgress({ done, total: bundle.photos.length });
            return;
          } catch (e: any) {
            if (attempt === 3) failed.push(`${p.filename} (${e?.message ?? "network"})`);
            else await new Promise(r => setTimeout(r, attempt * 800));
          }
        }
      }));

      if (done === 0) {
        throw new Error(`All ${bundle.photos.length} downloads failed (${failed[0] ?? "unknown"}). If this page has been open a while, refresh it and try again.`);
      }
      if (failed.length > 0) {
        zip.file("_MISSING_PHOTOS.txt",
          `The following photos couldn't be fetched:\n\n${failed.join("\n")}\n\nRefresh the page and download again, or grab them individually.\n`);
        alert(`Heads up: ${failed.length} of ${bundle.photos.length} photos couldn't be fetched — the zip lists them in _MISSING_PHOTOS.txt. Refresh the page and retry for the rest.`);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const sitePart = (siteCode ?? "SITE").replace(/[^A-Za-z0-9-]/g, "_");
      const zipName  = `${sitePart}_Bundle_${bundle.bundle_number}.zip`;

      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = zipName;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err: any) {
      console.error("[bundle zip] failed:", err);
      alert(err?.message ?? "Couldn't pack the bundle. Refresh the page and try again, or download photos individually.");
    } finally {
      setZipping(false);
      setZipProgress(null);
    }
  }

  return (
    <div className="bg-ink-card border border-line rounded-md overflow-hidden">
      <div className="flex items-center gap-3 p-4">
        <FolderOpen size={18} className="text-accent flex-shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-medium text-text">
            Bundle {bundle.bundle_number}
          </div>
          <div className="text-[11px] text-text-muted mt-0.5">
            {bundle.photos.length} photo{bundle.photos.length === 1 ? "" : "s"} · {sizeMb.toFixed(1)} MB
          </div>
        </div>
        <button
          type="button"
          onClick={downloadBundleAsZip}
          disabled={zipping}
          className="px-3 py-1.5 text-[12px] font-medium bg-accent text-white rounded hover:bg-accent-hover disabled:opacity-60 inline-flex items-center gap-1"
        >
          {zipping
            ? <><Loader2 size={12} className="animate-spin"/> {zipProgress ? `${zipProgress.done}/${zipProgress.total}` : "Packing…"}</>
            : <><Package size={12}/> Download ZIP</>}
        </button>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="text-[11px] text-text-muted hover:text-text px-2"
        >
          {open ? "Hide" : "Show"} files
        </button>
      </div>
      {open && (
        <div className="border-t border-line">
          <div className="divide-y divide-line">
            {bundle.photos.map(p => (
              <div key={p.asset_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-mono text-text truncate">{p.filename}</div>
                  <div className="text-[10px] text-text-muted">{fmtBytes(p.size_bytes)}</div>
                </div>
                {p.download_url && (
                  <a
                    href={p.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onDownload(p.filename)}
                    className="px-3 py-1.5 text-[11px] font-medium border border-accent/40 text-accent rounded hover:bg-accent/10 inline-flex items-center gap-1"
                  >
                    <Download size={12}/> Download
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DocRow({
  doc, onDownload,
}: {
  doc:        AcceptanceData["documents"][number];
  onDownload: (label: string) => void;
}) {
  return (
    <div className="bg-ink-card border border-line rounded-md p-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <FileText size={16} className="text-accent flex-shrink-0"/>
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-text truncate">{doc.filename}</div>
          <div className="text-[11px] text-text-muted mt-0.5">
            {DOC_TYPE_LABELS[doc.doc_type] ?? doc.doc_type}
            {doc.doc_type_label ? ` · ${doc.doc_type_label}` : ""}
            {doc.size_bytes != null ? ` · ${fmtBytes(doc.size_bytes)}` : ""}
          </div>
        </div>
      </div>
      {doc.download_url && (
        <a
          href={doc.download_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onDownload(doc.filename)}
          className="px-3 py-1.5 text-[11px] font-medium border border-accent/40 text-accent rounded hover:bg-accent/10 inline-flex items-center gap-1 flex-shrink-0"
        >
          <Download size={12}/> Download
        </a>
      )}
    </div>
  );
}
